import { flare } from "@flareapp/js";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import queryClient from "api/queryClient";
import { AppErrorBoundary, RouterErrorComponent } from "components/UI/ErrorBoundary/ErrorBoundary";
import { NotFound } from "components/UI/NotFound/NotFound";
import { TFunction } from "i18next";
import { AntdConfigProvider } from "lib/providers/AntdConfigProvider.tsx";
import { ClarityProvider } from "lib/providers/ClarityProvider.tsx";
import { TanStackRouterDevtools } from "lib/providers/TanStackRouterDevtools";
import { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import { RecoilEnv, RecoilRoot } from "recoil";
import { routeTree } from "types/routeTree.gen.ts";

import "@fontsource/open-sans"; // Defaults to weight 400
import "@fontsource/open-sans/400.css"; // Specify weight
import "@fontsource/open-sans/700.css"; // Specify weight
import "i18n/config";
import "react-toastify/dist/ReactToastify.css";
import "index.css";

// Initialize Flare for error tracking
if (import.meta.env.MODE === "staging" || import.meta.env.MODE === "production") {
    flare.light(import.meta.env.VITE_FLARE_API_KEY);
}

// Set up a Router instance
const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultNotFoundComponent: NotFound,
    // Custom error component that reports to Flare and shows our UI
    defaultErrorComponent: RouterErrorComponent,
    context: {
        t: {} as unknown as TFunction,
    },
});

// Register things for typesafety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false;

const rootElement = document.getElementById("root");
if (rootElement) {
    ReactDOM.createRoot(rootElement).render(<Main />);
} else {
    console.error("Root element not found.");
}

function Main() {
    const { t } = useTranslation();

    return (
        <AppErrorBoundary>
            <RecoilRoot>
                <QueryClientProvider client={queryClient}>
                    <AntdConfigProvider>
                        <ClarityProvider>
                            <RouterProvider context={{ t }} router={router} />
                        </ClarityProvider>
                        <Suspense>
                            <TanStackRouterDevtools router={router} />
                        </Suspense>
                    </AntdConfigProvider>
                    <ToastContainer position="bottom-right" />
                    <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
            </RecoilRoot>
        </AppErrorBoundary>
    );
}
